import { useEffect, useMemo, useState, type ReactNode, type CSSProperties } from "react";
import type { PrayerPhaseState } from "../lib/prayerPhase";

type ThemeVars = Record<string, string>;

function clamp01(n: number) {
  return Math.max(0, Math.min(1, n));
}

function makeVars(state: PrayerPhaseState | null): ThemeVars {
  const sun = clamp01(state?.sun.progress01 ?? 0.5);
  const sunX = 15 + sun * 70; // 15..85
  const sunY = 70 - sun * 45; // 70..25

  // Night intensity (0 day -> 1 night), softly inferred from sun progress.
  const night = sun < 0.06 || sun > 0.94 ? 1 : sun < 0.14 ? (0.14 - sun) / 0.08 : sun > 0.86 ? (sun - 0.86) / 0.08 : 0;

  const tint = state?.theme.tintRGB ?? "190 220 235";
  const tint2 = state?.theme.tint2RGB ?? "120 160 185";
  const accent = state?.theme.accentRGB ?? "210 255 235";
  const intensity = clamp01(state?.animationIntensity ?? 0.35);

  return {
    "--amb-tint": tint,
    "--amb-tint2": tint2,
    "--amb-accent": accent,
    "--amb-sun-x": `${sunX}%`,
    "--amb-sun-y": `${sunY}%`,
    "--amb-night": String(night),
    "--amb-intensity": String(intensity)
  };
}

export function AmbientShell(props: { state?: PrayerPhaseState | null; children: ReactNode }) {
  const vars = useMemo(() => makeVars(props.state ?? null), [props.state]);

  // Cross-fade between previous and current ambience whenever phase/theme changes.
  const [prevVars, setPrevVars] = useState<ThemeVars>(() => vars);
  const [swap, setSwap] = useState(false);

  useEffect(() => {
    // If no meaningful change, do nothing.
    const changed = JSON.stringify(vars) !== JSON.stringify(prevVars);
    if (!changed) return;

    // Set previous to old, then trigger overlay swap.
    setPrevVars((p) => p);
    // Next tick: flip swap to fade.
    setSwap((s) => !s);
    // After fade, lock prevVars to current for the next transition.
    const id = window.setTimeout(() => setPrevVars(vars), 1200);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vars]);

  // We render two layers: base uses prevVars, overlay uses vars. Opacity is toggled by `swap`.
  const baseStyle = prevVars;
  const overlayStyle = vars;

  return (
    <div className="ambientShell" style={vars as unknown as CSSProperties}>
      <div className="ambientLayer ambientLayerBase" style={baseStyle as unknown as CSSProperties} aria-hidden="true" />
      <div className={"ambientLayer ambientLayerTop" + (swap ? " isOn" : "")} style={overlayStyle as unknown as CSSProperties} aria-hidden="true" />
      <div className="ambientContent">{props.children}</div>
    </div>
  );
}
